import { useT } from '../../hooks/useT';

const CATEGORY_META = {
  investment:           { icon: '📈', color: 'brand' },
  expense_reduction:    { icon: '✂️', color: 'yellow' },
  budget_reallocation:  { icon: '♻️', color: 'purple' },
  behavior:             { icon: '🧠', color: 'emerald' },
};

function formatCurrency(value, currency = 'USD') {
  if (!value) return null;
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value).toLocaleString(locale, { style: 'currency', currency });
}

export default function AIInsights({ recommendations = [], riskWarnings = [], currency = 'USD' }) {
  const t = useT();
  return (
    <div className="space-y-6">
      {/* Risk Warnings */}
      {riskWarnings.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <span>⚠️</span> {t('insights.warnings.title').replace('⚠️ ', '')}
          </h4>
          <div className="space-y-2">
            {riskWarnings.map((warning, i) => (
              <div
                key={i}
                className="p-3 bg-yellow-900/10 border border-yellow-800/40 rounded-xl text-sm text-yellow-300"
              >
                {warning}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <span>✦</span> {t('insights.recs.title').replace('✦ ', '')}
          </h4>
          <div className="space-y-3">
            {recommendations.map((rec, i) => {
              const meta = CATEGORY_META[rec.category] || CATEGORY_META.behavior;
              return (
                <div key={i} className="p-4 bg-surface-hover rounded-xl border border-surface-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg mt-0.5">{meta.icon}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-slate-200">{rec.title}</p>
                          <span className="text-xs text-slate-500 bg-surface rounded px-1.5 py-0.5">
                            {t(`insights.cat.${rec.category}`) || meta.icon}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 leading-relaxed">{rec.description}</p>
                      </div>
                    </div>
                    {rec.potential_impact && (
                      <div className="text-right shrink-0">
                        <p className="text-xs text-slate-500">{t('insights.potential_save')}</p>
                        <p className="text-sm font-semibold text-emerald-400">
                          +{formatCurrency(rec.potential_impact, currency)}/mo
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-xs text-slate-600">{t('insights.priority')}</span>
                    <span className="text-xs text-slate-500">{t(`insights.priority.${rec.priority}`) || rec.priority}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {recommendations.length === 0 && riskWarnings.length === 0 && (
        <p className="text-sm text-slate-500">{t('insights.empty')}</p>
      )}
    </div>
  );
}
